import React, { useState, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import usePageSEO from '../hooks/usePageSEO';
import {
  ArrowRight, ArrowLeft, Check, Upload, X, Camera,
  Scissors, User, Calendar, Heart, Sparkles, Clock,
  Mail, Loader2, ChevronRight, Pill
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Translations ───
const t = {
  de: {
    badge: 'Unverbindliche Anfrage',
    title: 'Kosten',
    titleAccent: 'rechner',
    subtitle: 'In wenigen Schritten zu Ihrem individuellen Angebot',
    stepOf: 'Schritt {n} von {total}',
    next: 'Weiter',
    back: 'Zurück',
    submit: 'Anfrage absenden',
    submitting: 'Wird gesendet...',
    successTitle: 'Vielen Dank!',
    successText: 'Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns schnellstmöglich mit einem individuellen Angebot bei Ihnen.',
    backToHome: 'Zurück zur Startseite',
    // Step 1
    s1Title: 'Welche Behandlung interessiert Sie?',
    s1Sub: 'Wählen Sie den gewünschten Behandlungsbereich',
    treatHaar: 'Haartransplantation',
    treatHaarDesc: 'FUE, DHI & Hybrid-Techniken',
    treatBart: 'Barttransplantation',
    treatBartDesc: 'Vollbart, Kontur & Verdichtung',
    treatAugen: 'Augenbrauentransplantation',
    treatAugenDesc: 'Natürliche Brauen-Rekonstruktion',
    // Step 2 Gender
    s2Title: 'Ihr Geschlecht',
    s2Sub: 'Für die passenden Haarausfallmuster',
    male: 'Mann',
    female: 'Frau',
    // Step 3 Pattern
    s3TitleHaar: 'Haarausfallmuster',
    s3TitleBart: 'Bartbereich',
    s3TitleAugen: 'Augenbrauen-Zustand',
    s3Sub: 'Welches Bild entspricht Ihrem Zustand am besten?',
    // Norwood
    norwood12: 'Norwood 1-2', norwood12sub: 'Beginnend',
    norwood3: 'Norwood 3', norwood3sub: 'Geheimratsecken',
    norwood4: 'Norwood 4', norwood4sub: 'Fortgeschritten',
    norwood56: 'Norwood 5-6', norwood56sub: 'Stark ausgeprägt',
    norwood7: 'Norwood 7', norwood7sub: 'Sehr stark',
    ludwig: 'Lichter Scheitel', ludwigsub: 'Weiblich',
    bartKomplett: 'Vollbart', bartKomplettsub: 'Komplett',
    bartUndicht: 'Undichter Bart', bartUndichtsub: 'Lücken',
    bartKontur: 'Kontur', bartKontursub: 'Definition',
    bartLuecken: 'Lücken', bartLueckensub: 'Kahle Stellen',
    augenKahl: 'Kahle Brauen', augenKahlsub: 'Kein Wuchs',
    augenUndicht: 'Undicht', augenUndichtsub: 'Lückenhaft',
    augenMicro: 'Microblading', augenMicrosub: 'Korrektur',
    // Step 4 Personal
    s4Title: 'Persönliche Angaben',
    s4Sub: 'Helfen uns bei der Einschätzung',
    age: 'Alter',
    agePlaceholder: 'z.B. 35',
    hairLossSince: 'Seit wann besteht der Haarausfall?',
    since1: 'Unter 1 Jahr', since2: '1-3 Jahre', since3: '3-5 Jahre', since4: '5-10 Jahre', since5: 'Über 10 Jahre',
    preExisting: 'Vorerkrankungen?',
    preExistingPlaceholder: 'Welche?',
    previousTransplant: 'Bereits eine Transplantation gehabt?',
    yes: 'Ja', no: 'Nein',
    // Haar-specific treatments
    currentMedication: 'Aktuelle/bisherige Behandlung?',
    medFinasterid: 'Finasterid',
    medMinoxidil: 'Minoxidil',
    medPigment: 'Pigmentierung',
    medNone: 'Keine',
    // Augenbrauen-specific
    previousBrowTreatment: 'Vorherige Behandlung der Augenbrauen?',
    browMicroblading: 'Microblading',
    browLaser: 'Laserbehandlung',
    browPMU: 'Permanent Make-Up',
    browNone: 'Keine',
    // Step 5 Extras
    s5Title: 'Methode & Zusatzoptionen',
    s5Sub: 'Wählen Sie Ihre bevorzugte Methode',
    methodTitle: 'Transplantationsmethode',
    methodClassic: 'Klassisch (FUE Saphir)',
    methodClassicDesc: 'Komplettrasur erforderlich — bewährte Standardmethode',
    methodClassicInfo: 'Der gesamte Kopf wird rasiert. Die Kanäle für die Grafts werden mit der Saphir-Klinge geöffnet und die Grafts einzeln eingesetzt.',
    methodDHI: 'Ohne Rasur Oberkopf (Saphir-DHI Hybrid)',
    methodDHIDesc: 'Nur der Spenderbereich wird rasiert — das Deckhaar bleibt',
    methodDHIInfo: 'Am Hinterkopf wird ein schmaler Streifen rasiert. Die Grafts werden mit DHI-Implanter direkt ins bestehende Haar eingesetzt.',
    methodLongHair: 'Long Hair FUE (komplett ohne Rasur)',
    methodLongHairDesc: 'Keine Rasur nötig — Premium-Methode',
    methodLongHairInfo: 'Die Haare werden in voller Länge entnommen und eingesetzt. Kein sichtbarer Eingriff — ideal bei kurzen Haarpausen.',
    extrasTitle: 'Zusatzoptionen',
    painFree: 'Schmerzfreie Betäubung (Dion Painless Injection)',
    painFreeDesc: 'Schmerzfreie Anästhesie für maximalen Komfort',
    prp: 'PRP-Behandlung zur Nachsorge',
    prpDesc: 'Beschleunigt die Heilung und stärkt die Grafts',
    // Step 6 Timeline
    s6Title: 'Wunschtermin',
    s6Sub: 'Wann möchten Sie den Eingriff?',
    timeAsap: 'So schnell wie möglich',
    time13: 'In 1-3 Monaten',
    time36: 'In 3-6 Monaten',
    time612: 'In 6-12 Monaten',
    timeUnsure: 'Weiß noch nicht',
    // Step 7 Photos
    s7Title: 'Fotos hochladen',
    s7Sub: 'Optional — für eine genauere Einschätzung',
    photoChoose: 'Foto wählen',
    photoHaarFront: 'Vorne (Stirn)',
    photoHaarTop: 'Oben (Scheitel)',
    photoHaarBack: 'Hinterkopf',
    photoBartFace: 'Bartbereich',
    photoBartBack: 'Hinterkopf (Spender)',
    photoAugen: 'Augenbrauen frontal',
    // Step 8 Contact
    s8Title: 'Kontaktdaten',
    s8Sub: 'Damit wir Ihnen ein Angebot zusenden können',
    name: 'Name',
    namePlaceholder: 'Ihr vollständiger Name',
    email: 'E-Mail',
    emailPlaceholder: 'ihre@email.de',
    phone: 'Telefon',
    phoneNote: '(freiwillig)',
    referralTitle: 'Wie sind Sie auf uns aufmerksam geworden?',
    referralNote: '(freiwillig)',
    referralOptions: { google: 'Google', instagram: 'Instagram', tiktok: 'TikTok', empfehlung: 'Empfehlung', werbetafel: 'Werbetafel', sonstiges: 'Sonstiges' },
    phonePlaceholder: '+49 170 ...',
  },
  en: {
    badge: 'Non-binding inquiry',
    title: 'Price',
    titleAccent: 'Calculator',
    subtitle: 'Get your individual quote in just a few steps',
    stepOf: 'Step {n} of {total}',
    next: 'Next',
    back: 'Back',
    submit: 'Submit inquiry',
    submitting: 'Sending...',
    successTitle: 'Thank you!',
    successText: 'Your inquiry has been sent successfully. We will get back to you with an individual offer as soon as possible.',
    backToHome: 'Back to homepage',
    s1Title: 'Which treatment are you interested in?',
    s1Sub: 'Choose your desired treatment area',
    treatHaar: 'Hair Transplant',
    treatHaarDesc: 'FUE, DHI & Hybrid techniques',
    treatBart: 'Beard Transplant',
    treatBartDesc: 'Full beard, contour & densification',
    treatAugen: 'Eyebrow Transplant',
    treatAugenDesc: 'Natural brow reconstruction',
    s2Title: 'Your gender',
    s2Sub: 'To show the matching hair loss patterns',
    male: 'Male',
    female: 'Female',
    s3TitleHaar: 'Hair loss pattern',
    s3TitleBart: 'Beard area',
    s3TitleAugen: 'Eyebrow condition',
    s3Sub: 'Which image best matches your condition?',
    norwood12: 'Norwood 1-2', norwood12sub: 'Early stage',
    norwood3: 'Norwood 3', norwood3sub: 'Receding',
    norwood4: 'Norwood 4', norwood4sub: 'Advanced',
    norwood56: 'Norwood 5-6', norwood56sub: 'Severe',
    norwood7: 'Norwood 7', norwood7sub: 'Very severe',
    ludwig: 'Thinning crown', ludwigsub: 'Female pattern',
    bartKomplett: 'Full beard', bartKomplettsub: 'Complete',
    bartUndicht: 'Patchy beard', bartUndichtsub: 'Gaps',
    bartKontur: 'Contour', bartKontursub: 'Definition',
    bartLuecken: 'Gaps', bartLueckensub: 'Bald spots',
    augenKahl: 'No brows', augenKahlsub: 'No growth',
    augenUndicht: 'Sparse', augenUndichtsub: 'Patchy',
    augenMicro: 'Microblading', augenMicrosub: 'Correction',
    s4Title: 'Personal details',
    s4Sub: 'Help us with the assessment',
    age: 'Age',
    agePlaceholder: 'e.g. 35',
    hairLossSince: 'How long have you experienced hair loss?',
    since1: 'Under 1 year', since2: '1-3 years', since3: '3-5 years', since4: '5-10 years', since5: 'Over 10 years',
    preExisting: 'Pre-existing conditions?',
    preExistingPlaceholder: 'Which ones?',
    previousTransplant: 'Had a transplant before?',
    yes: 'Yes', no: 'No',
    currentMedication: 'Current/previous treatment?',
    medFinasterid: 'Finasteride',
    medMinoxidil: 'Minoxidil',
    medPigment: 'Pigmentation',
    medNone: 'None',
    previousBrowTreatment: 'Previous eyebrow treatment?',
    browMicroblading: 'Microblading',
    browLaser: 'Laser treatment',
    browPMU: 'Permanent makeup',
    browNone: 'None',
    s5Title: 'Method & Additional Options',
    s5Sub: 'Choose your preferred method',
    methodTitle: 'Transplant Method',
    methodClassic: 'Classic (FUE Sapphire)',
    methodClassicDesc: 'Full shave required — standard proven method',
    methodClassicInfo: 'The entire head is shaved. Channels for the grafts are opened with the sapphire blade and grafts are implanted individually.',
    methodDHI: 'No Shave Top (Sapphire-DHI Hybrid)',
    methodDHIDesc: 'Only the donor area is shaved — your top hair stays',
    methodDHIInfo: 'A narrow strip at the back is shaved. Grafts are implanted directly into existing hair with DHI implanter.',
    methodLongHair: 'Long Hair FUE (completely no shave)',
    methodLongHairDesc: 'No shaving at all — premium method',
    methodLongHairInfo: 'Hair is extracted and implanted at full length. No visible procedure — ideal for minimal downtime.',
    extrasTitle: 'Additional Options',
    painFree: 'Pain-free anesthesia (Dion Painless Injection)',
    painFreeDesc: 'Pain-free anesthesia for maximum comfort',
    prp: 'PRP aftercare treatment',
    prpDesc: 'Accelerates healing and strengthens grafts',
    s6Title: 'Preferred date',
    s6Sub: 'When would you like the procedure?',
    timeAsap: 'As soon as possible',
    time13: 'In 1-3 months',
    time36: 'In 3-6 months',
    time612: 'In 6-12 months',
    timeUnsure: 'Not sure yet',
    s7Title: 'Upload photos',
    s7Sub: 'Optional — for a more accurate assessment',
    photoChoose: 'Choose photo',
    photoHaarFront: 'Front (forehead)',
    photoHaarTop: 'Top (crown)',
    photoHaarBack: 'Back of head',
    photoBartFace: 'Beard area',
    photoBartBack: 'Back of head (donor)',
    photoAugen: 'Eyebrows frontal',
    s8Title: 'Contact details',
    s8Sub: 'So we can send you a quote',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone',
    phoneNote: '(optional)',
    referralTitle: 'How did you hear about us?',
    referralNote: '(optional)',
    referralOptions: { google: 'Google', instagram: 'Instagram', tiktok: 'TikTok', empfehlung: 'Recommendation', werbetafel: 'Billboard', sonstiges: 'Other' },
    phonePlaceholder: '+49 170 ...',
  },
};

// ─── Pattern data ───
const getPatterns = (lang) => ({
  haar_mann: [
    { id: 'norwood_1_2', label: t[lang].norwood12, sub: t[lang].norwood12sub, image: '/images/norwood_scale_1.webp' },
    { id: 'norwood_3', label: t[lang].norwood3, sub: t[lang].norwood3sub, image: '/images/norwood_scale_3.webp' },
    { id: 'norwood_4', label: t[lang].norwood4, sub: t[lang].norwood4sub, image: '/images/norwood_scale_4.webp' },
    { id: 'norwood_5_6', label: t[lang].norwood56, sub: t[lang].norwood56sub, image: '/images/norwood_scale_5.webp' },
    { id: 'norwood_7', label: t[lang].norwood7, sub: t[lang].norwood7sub, image: '/images/norwood_scale_7.webp' },
  ],
  haar_frau: [
    { id: 'lichter_scheitel', label: t[lang].ludwig, sub: t[lang].ludwigsub, image: '/images/frau_lichter_scheitel.webp' },
  ],
  bart: [
    { id: 'komplett', label: t[lang].bartKomplett, sub: t[lang].bartKomplettsub, image: '/images/Bart_komplett.webp' },
    { id: 'undicht', label: t[lang].bartUndicht, sub: t[lang].bartUndichtsub, image: '/images/Bart_undicht.webp' },
    { id: 'kontur', label: t[lang].bartKontur, sub: t[lang].bartKontursub, image: '/images/Bart_Kontur.webp' },
    { id: 'luecken', label: t[lang].bartLuecken, sub: t[lang].bartLueckensub, image: '/images/Bart_L%C3%BCcken.webp' },
  ],
  augenbrauen: [
    { id: 'kahl', label: t[lang].augenKahl, sub: t[lang].augenKahlsub, image: '/images/Augenbrauen_kahl.webp' },
    { id: 'undicht', label: t[lang].augenUndicht, sub: t[lang].augenUndichtsub, image: '/images/Augenbrauen_undicht.webp' },
    { id: 'microblading', label: t[lang].augenMicro, sub: t[lang].augenMicrosub, image: '/images/Augenbrauen_Microblading.webp' },
  ],
});

const PHOTO_LABELS = (lang) => ({
  haar: [t[lang].photoHaarFront, t[lang].photoHaarTop, t[lang].photoHaarBack],
  bart: [t[lang].photoBartFace, t[lang].photoBartBack],
  augenbrauen: [t[lang].photoAugen],
});

// Step IDs
const ALL_STEPS = ['treatment', 'gender', 'pattern', 'personal', 'extras', 'timeline', 'photos', 'contact'];

const PriceCalculator = () => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const l = t[language] || t.de;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState({
    treatment: '', gender: '', pattern: '',
    age: '', hairLossSince: '',
    preExisting: 'nein', preExistingDetails: '',
    previousTransplant: 'nein',
    currentMedications: [],
    previousBrowTreatments: [],
    method: 'classic',
    painFreeAnesthesia: '', prpTreatment: '',
    timeline: '', name: '', email: '', phone: '',
    images: {},
  });

  const set = useCallback((key, val) => setData(d => ({ ...d, [key]: val })), []);

  const toggleArrayItem = useCallback((key, item) => {
    setData(d => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item] };
    });
  }, []);

  // Active steps (filter based on treatment)
  const activeSteps = ALL_STEPS.filter(s => {
    if (s === 'gender') return data.treatment === 'haar';
    if (s === 'extras') return data.treatment !== 'augenbrauen';
    return true;
  });

  const currentStepId = activeSteps[step];
  const totalSteps = activeSteps.length;
  const progress = totalSteps <= 1 ? 100 : (step / (totalSteps - 1)) * 100;

  const canNext = () => {
    switch (currentStepId) {
      case 'treatment': return !!data.treatment;
      case 'gender': return !!data.gender;
      case 'pattern': return !!data.pattern;
      case 'personal': return !!data.age && (data.treatment !== 'haar' || !!data.hairLossSince);
      case 'extras': return true;
      case 'timeline': return !!data.timeline;
      case 'photos': return true;
      case 'contact': return !!data.name && !!data.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
      default: return false;
    }
  };

  const scrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const next = () => { if (step < totalSteps - 1 && canNext()) { setStep(s => s + 1); scrollToTop(); } };
  const prev = () => { if (step > 0) { setStep(s => s - 1); scrollToTop(); } };

  const handleImageUpload = (label, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('Max 10 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setData(d => ({ ...d, images: { ...d.images, [label]: reader.result.split(',')[1] } }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (label) => {
    setData(d => { const imgs = { ...d.images }; delete imgs[label]; return { ...d, images: imgs }; });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        method: data.treatment === 'augenbrauen' ? 'longhair' : data.method,
        currentMedications: data.currentMedications.join(', '),
        previousBrowTreatments: data.previousBrowTreatments.join(', '),
      };
      const res = await fetch(`${API_URL}/api/price-calculator`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        if (typeof window.gtag_report_preisrechner === 'function') {
          window.gtag_report_preisrechner();
        }
      }
      else throw new Error('Failed');
    } catch { alert(language === 'de' ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.' : 'Error sending. Please try again.');
    } finally { setSubmitting(false); }
  };

  // ─── Shared styles ───
  const card = `p-4 rounded-xl border cursor-pointer transition-all duration-200 ${isDark ? 'border-white/10 hover:border-[#7BA7C2]/50' : 'border-gray-200 hover:border-[#5A8BA6]/50'}`;
  const sel = isDark ? 'border-[#7BA7C2] bg-[#7BA7C2]/10 ring-1 ring-[#7BA7C2]/30' : 'border-[#5A8BA6] bg-[#5A8BA6]/10 ring-1 ring-[#5A8BA6]/30';
  const inp = `w-full px-4 py-3 rounded-xl border outline-none transition-all focus:ring-2 ${isDark ? 'bg-white/5 border-white/10 text-white focus:ring-[#7BA7C2]/30 placeholder:text-gray-500' : 'bg-white border-gray-200 text-gray-900 focus:ring-[#5A8BA6]/30 placeholder:text-gray-400'}`;
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDark ? 'text-gray-500' : 'text-gray-400';
  const textLabel = isDark ? 'text-gray-300' : 'text-gray-700';

  // ─── STEP RENDERS ───
  const renderTreatment = () => (
    <div>
      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s1Title}</h3>
      <p className={`mb-6 text-sm ${textSecondary}`}>{l.s1Sub}</p>
      <div className="grid gap-3">
        {[
          { id: 'haar', label: l.treatHaar, desc: l.treatHaarDesc },
          { id: 'bart', label: l.treatBart, desc: l.treatBartDesc },
          { id: 'augenbrauen', label: l.treatAugen, desc: l.treatAugenDesc },
        ].map(item => (
          <button key={item.id} onClick={() => { set('treatment', item.id); set('pattern', ''); set('currentMedications', []); set('previousBrowTreatments', []); }}
            data-testid={`treatment-${item.id}`}
            className={`${card} text-left flex items-center gap-4 ${data.treatment === item.id ? sel : ''}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${data.treatment === item.id ? 'bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6]' : isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <Scissors className={`w-5 h-5 ${data.treatment === item.id ? 'text-white' : textMuted}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-semibold ${textPrimary}`}>{item.label}</div>
              <div className={`text-xs ${textMuted}`}>{item.desc}</div>
            </div>
            {data.treatment === item.id && <Check className="w-5 h-5 text-[#7BA7C2] flex-shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );

  const renderGender = () => (
    <div>
      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s2Title}</h3>
      <p className={`mb-6 text-sm ${textSecondary}`}>{l.s2Sub}</p>
      <div className="grid grid-cols-2 gap-3">
        {[{ id: 'mann', label: l.male }, { id: 'frau', label: l.female }].map(g => (
          <button key={g.id} onClick={() => { set('gender', g.id); set('pattern', ''); }}
            data-testid={`gender-${g.id}`}
            className={`${card} text-center py-6 ${data.gender === g.id ? sel : ''}`}>
            <User className={`w-8 h-8 mx-auto mb-2 ${data.gender === g.id ? 'text-[#7BA7C2]' : textMuted}`} />
            <div className={`font-semibold ${textPrimary}`}>{g.label}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderPattern = () => {
    const key = data.treatment === 'haar' ? `haar_${data.gender || 'mann'}` : data.treatment;
    const patterns = getPatterns(language)[key] || [];
    const title = data.treatment === 'haar' ? l.s3TitleHaar : data.treatment === 'bart' ? l.s3TitleBart : l.s3TitleAugen;
    return (
      <div>
        <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{title}</h3>
        <p className={`mb-6 text-sm ${textSecondary}`}>{l.s3Sub}</p>
        <div className={`grid gap-3 ${patterns.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {patterns.map(p => (
            <button key={p.id} onClick={() => set('pattern', p.label)} data-testid={`pattern-${p.id}`}
              className={`${card} text-center group overflow-hidden flex flex-col ${data.pattern === p.label ? sel : ''}`}>
              <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 bg-gray-100">
                <img src={p.image} alt={p.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div className="flex-1 flex flex-col justify-center min-h-[40px]">
                <div className={`font-semibold text-sm ${textPrimary}`}>{p.label}</div>
                <div className={`text-xs mt-0.5 ${textMuted}`}>{p.sub}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderPersonal = () => (
    <div>
      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s4Title}</h3>
      <p className={`mb-6 text-sm ${textSecondary}`}>{l.s4Sub}</p>
      <div className="space-y-4">
        {/* Age */}
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.age} *</label>
          <input type="number" min="18" max="80" placeholder={l.agePlaceholder} value={data.age}
            onChange={e => set('age', e.target.value)} className={inp} data-testid="input-age" />
        </div>

        {/* Hair loss since — only for Haar */}
        {data.treatment === 'haar' && (
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.hairLossSince} *</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[l.since1, l.since2, l.since3, l.since4, l.since5].map((o, i) => (
                <button key={o} onClick={() => set('hairLossSince', o)} data-testid={`hairloss-${i}`}
                  className={`${card} text-center text-sm py-2.5 ${data.hairLossSince === o ? sel : ''}`}>{o}</button>
              ))}
            </div>
          </div>
        )}

        {/* Pre-existing conditions */}
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.preExisting}</label>
          <div className="flex gap-2 mb-2">
            {['nein', 'ja'].map(v => (
              <button key={v} onClick={() => set('preExisting', v)}
                className={`${card} flex-1 text-center text-sm py-2.5 ${data.preExisting === v ? sel : ''}`}>
                {v === 'ja' ? l.yes : l.no}
              </button>
            ))}
          </div>
          {data.preExisting === 'ja' && (
            <input placeholder={l.preExistingPlaceholder} value={data.preExistingDetails}
              onChange={e => set('preExistingDetails', e.target.value)} className={`${inp} mt-2`} data-testid="input-conditions" />
          )}
        </div>

        {/* Previous transplant */}
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.previousTransplant}</label>
          <div className="flex gap-2">
            {['nein', 'ja'].map(v => (
              <button key={v} onClick={() => set('previousTransplant', v)}
                className={`${card} flex-1 text-center text-sm py-2.5 ${data.previousTransplant === v ? sel : ''}`}>
                {v === 'ja' ? l.yes : l.no}
              </button>
            ))}
          </div>
        </div>

        {/* Haar: Current medications */}
        {data.treatment === 'haar' && (
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.currentMedication}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'finasterid', label: l.medFinasterid },
                { id: 'minoxidil', label: l.medMinoxidil },
                { id: 'pigment', label: l.medPigment },
                { id: 'none', label: l.medNone },
              ].map(m => (
                <button key={m.id} onClick={() => {
                  if (m.id === 'none') set('currentMedications', ['none']);
                  else { const arr = (data.currentMedications || []).filter(i => i !== 'none');
                    set('currentMedications', arr.includes(m.id) ? arr.filter(i => i !== m.id) : [...arr, m.id]); }
                }} data-testid={`med-${m.id}`}
                  className={`${card} flex items-center justify-center gap-2 text-sm py-2.5 ${
                    (data.currentMedications || []).includes(m.id) ? sel : ''
                  }`}>
                  <Pill className={`w-4 h-4 ${(data.currentMedications || []).includes(m.id) ? 'text-[#7BA7C2]' : textMuted}`} />
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Augenbrauen: Previous brow treatments */}
        {data.treatment === 'augenbrauen' && (
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.previousBrowTreatment}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'microblading', label: l.browMicroblading },
                { id: 'laser', label: l.browLaser },
                { id: 'pmu', label: l.browPMU },
                { id: 'none', label: l.browNone },
              ].map(b => (
                <button key={b.id} onClick={() => {
                  if (b.id === 'none') set('previousBrowTreatments', ['none']);
                  else { const arr = (data.previousBrowTreatments || []).filter(i => i !== 'none');
                    set('previousBrowTreatments', arr.includes(b.id) ? arr.filter(i => i !== b.id) : [...arr, b.id]); }
                }} data-testid={`brow-${b.id}`}
                  className={`${card} text-center text-sm py-2.5 ${
                    (data.previousBrowTreatments || []).includes(b.id) ? sel : ''
                  }`}>
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderExtras = () => {
    const methods = [
      { id: 'classic', label: l.methodClassic, desc: l.methodClassicDesc, info: l.methodClassicInfo },
      { id: 'dhi', label: l.methodDHI, desc: l.methodDHIDesc, info: l.methodDHIInfo },
      { id: 'longhair', label: l.methodLongHair, desc: l.methodLongHairDesc, info: l.methodLongHairInfo },
    ];

    return (
      <div>
        <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s5Title}</h3>
        <p className={`mb-5 text-sm ${textSecondary}`}>{l.s5Sub}</p>

        {/* Method Selection */}
        <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${textMuted}`}>{l.methodTitle}</div>
        <div className="space-y-3 mb-6">
          {methods.map(m => (
            <button key={m.id} onClick={() => set('method', m.id)} data-testid={`method-${m.id}`}
              className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
                data.method === m.id
                  ? isDark ? 'border-[#7BA7C2]/50 bg-[#7BA7C2]/10' : 'border-[#5A8BA6]/40 bg-[#5A8BA6]/5'
                  : isDark ? 'border-white/5 bg-white/[0.02] hover:border-white/10' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      data.method === m.id ? 'border-[#7BA7C2]' : isDark ? 'border-white/20' : 'border-gray-300'
                    }`}>
                      {data.method === m.id && <div className="w-2 h-2 rounded-full bg-[#7BA7C2]" />}
                    </div>
                    <span className={`font-semibold text-sm ${textPrimary}`}>{m.label}</span>
                  </div>
                  <div className={`text-xs mt-1 ml-6 ${textMuted}`}>{m.desc}</div>
                </div>
              </div>
              {/* Info box */}
              {data.method === m.id && (
                <div className={`mt-3 ml-6 p-3 rounded-lg text-xs leading-relaxed ${
                  isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-50 text-gray-500'
                }`}>
                  {m.info}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Additional extras (only for haar) */}
        {data.treatment === 'haar' && (
          <>
            <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${textMuted}`}>{l.extrasTitle}</div>
            <div className="space-y-3">
              {[
                { key: 'painFreeAnesthesia', label: l.painFree, desc: l.painFreeDesc },
                { key: 'prpTreatment', label: l.prp, desc: l.prpDesc },
              ].map(opt => (
                <div key={opt.key} className={`${card} ${data[opt.key] ? sel : ''}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm ${textPrimary}`}>{opt.label}</div>
                      <div className={`text-xs mt-0.5 ${textMuted}`}>{opt.desc}</div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {['ja', 'nein'].map(v => (
                        <button key={v} onClick={() => set(opt.key, v)} data-testid={`${opt.key}-${v}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            data[opt.key] === v
                              ? v === 'ja' ? 'bg-[#7BA7C2] text-white' : isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
                              : isDark ? 'bg-white/5 text-gray-500 hover:bg-white/10' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}>
                          {v === 'ja' ? l.yes : l.no}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTimeline = () => (
    <div>
      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s6Title}</h3>
      <p className={`mb-6 text-sm ${textSecondary}`}>{l.s6Sub}</p>
      <div className="grid gap-3">
        {[
          { id: 'asap', label: l.timeAsap },
          { id: '1-3', label: l.time13 },
          { id: '3-6', label: l.time36 },
          { id: '6-12', label: l.time612 },
          { id: 'unsure', label: l.timeUnsure },
        ].map(item => (
          <button key={item.id} onClick={() => set('timeline', item.id)} data-testid={`timeline-${item.id}`}
            className={`${card} flex items-center gap-3 ${data.timeline === item.id ? sel : ''}`}>
            <Clock className={`w-5 h-5 flex-shrink-0 ${data.timeline === item.id ? 'text-[#7BA7C2]' : textMuted}`} />
            <span className={`font-medium text-sm ${textPrimary}`}>{item.label}</span>
            {data.timeline === item.id && <Check className="w-4 h-4 text-[#7BA7C2] ml-auto" />}
          </button>
        ))}
      </div>
    </div>
  );

  const renderPhotos = () => {
    const labels = PHOTO_LABELS(language)[data.treatment] || PHOTO_LABELS(language).haar;
    return (
      <div>
        <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s7Title}</h3>
        <p className={`mb-6 text-sm ${textSecondary}`}>{l.s7Sub}</p>
        <div className="grid gap-4">
          {labels.map(label => (
            <div key={label}>
              <label className={`block text-sm font-medium mb-2 ${textLabel}`}>{label}</label>
              {data.images[label] ? (
                <div className="relative inline-block">
                  <img src={`data:image/jpeg;base64,${data.images[label]}`} alt={label}
                    className="w-24 h-24 object-cover rounded-xl border border-[#7BA7C2]/30" />
                  <button onClick={() => removeImage(label)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <label className={`flex items-center justify-center gap-2 ${card} cursor-pointer py-6`}>
                  <Upload className={`w-5 h-5 ${textMuted}`} />
                  <span className={`text-sm ${textSecondary}`}>{l.photoChoose}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(label, e)} />
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContact = () => (
    <div>
      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${textPrimary}`}>{l.s8Title}</h3>
      <p className={`mb-6 text-sm ${textSecondary}`}>{l.s8Sub}</p>
      <div className="space-y-4">
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.name} *</label>
          <input placeholder={l.namePlaceholder} value={data.name} onChange={e => set('name', e.target.value)}
            className={inp} data-testid="input-name" />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>{l.email} *</label>
          <input type="email" placeholder={l.emailPlaceholder} value={data.email} onChange={e => set('email', e.target.value)}
            className={inp} data-testid="input-email" />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>
            {l.phone} <span className={`text-xs ${textMuted}`}>{l.phoneNote}</span>
          </label>
          <input type="tel" placeholder={l.phonePlaceholder} value={data.phone} onChange={e => set('phone', e.target.value)}
            className={inp} data-testid="input-phone" />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${textLabel}`}>
            {l.referralTitle} <span className={`text-xs ${textMuted}`}>{l.referralNote}</span>
          </label>
          <div className="flex flex-wrap gap-2" data-testid="referral-source-options">
            {Object.entries(l.referralOptions).map(([id, label]) => (
              <button key={id} type="button" data-testid={`referral-${id}`}
                onClick={() => set('referralSource', data.referralSource === id ? '' : id)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${
                  data.referralSource === id
                    ? 'border-[#7BA7C2] bg-[#7BA7C2] text-white'
                    : isDark
                      ? 'border-white/20 text-gray-300 hover:border-[#7BA7C2]/60'
                      : 'border-gray-300 text-gray-600 hover:border-[#7BA7C2]/60'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] flex items-center justify-center mx-auto mb-6">
        <Check className="w-8 h-8 text-white" />
      </div>
      <h3 className={`text-2xl font-bold mb-3 ${textPrimary}`}>{l.successTitle}</h3>
      <p className={`mb-6 ${textSecondary}`}>{l.successText}</p>
      <a href="/" className="inline-flex items-center gap-2 text-[#7BA7C2] hover:underline font-medium">
        {l.backToHome} <ChevronRight className="w-4 h-4" />
      </a>
    </div>
  );

  const renderStep = () => {
    if (submitted) return renderSuccess();
    switch (currentStepId) {
      case 'treatment': return renderTreatment();
      case 'gender': return renderGender();
      case 'pattern': return renderPattern();
      case 'personal': return renderPersonal();
      case 'extras': return renderExtras();
      case 'timeline': return renderTimeline();
      case 'photos': return renderPhotos();
      case 'contact': return renderContact();
      default: return null;
    }
  };

  const isLast = step === totalSteps - 1;
  const stepLabel = l.stepOf.replace('{n}', step + 1).replace('{total}', totalSteps);

  usePageSEO({
    title: language === 'de' ? 'Preisrechner Haartransplantation | Kosten berechnen | Dion Hair Clinic' : 'Hair Transplant Price Calculator | Calculate Costs | Dion Hair Clinic',
    description: language === 'de' ? 'Berechne die Kosten deiner Haartransplantation, Barttransplantation oder Augenbrauentransplantation bei der Dion Hair Clinic in Mönchengladbach. Individuelles Angebot in wenigen Schritten.' : 'Calculate the costs of your hair transplant, beard transplant or eyebrow transplant at Dion Hair Clinic in Mönchengladbach. Individual offer in a few steps.',
    keywords: language === 'de' ? 'Haartransplantation Kosten, Haartransplantation Preise, Barttransplantation Kosten, Augenbrauentransplantation Preise, FUE Kosten Deutschland, DHI Preise Mönchengladbach, Haartransplantation Preisrechner' : 'hair transplant costs, hair transplant prices, beard transplant cost, eyebrow transplant prices, FUE cost Germany, DHI prices Mönchengladbach, hair transplant calculator',
    canonical: 'https://dionhairclinic.de/preisrechner',
    ogTitle: language === 'de' ? 'Haartransplantation Kosten berechnen | Dion Hair Clinic' : 'Calculate Hair Transplant Costs | Dion Hair Clinic',
    ogDescription: language === 'de' ? 'Erhalte ein individuelles Angebot für deine Haartransplantation. FUE & DHI Saphir-Technologie in Mönchengladbach.' : 'Get an individual offer for your hair transplant. FUE & DHI Sapphire technology in Mönchengladbach.',
    ogUrl: 'https://dionhairclinic.de/preisrechner',
    jsonLd: {
      "@context": "https://schema.org", "@type": "WebApplication",
      "name": language === 'de' ? "Preisrechner Haartransplantation" : "Hair Transplant Price Calculator",
      "url": "https://dionhairclinic.de/preisrechner", "applicationCategory": "HealthApplication",
      "provider": { "@type": "MedicalBusiness", "name": "Dion Hair Clinic",
        "address": { "@type": "PostalAddress", "addressLocality": "Mönchengladbach", "addressCountry": "DE" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "140", "bestRating": "5" }
      }
    },
  });

  return (
      <section className={`min-h-screen pt-20 sm:pt-24 pb-16 transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}
        data-testid="price-calculator">
      <div className="max-w-xl mx-auto px-4">
        {!submitted && (
          <>
            <div className="text-center mb-8">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 ${isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'}`}>
                <Sparkles className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>{l.badge}</span>
              </div>
              <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${textPrimary}`}>
                {l.title}<span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">{l.titleAccent}</span>
              </h1>
              <p className={`text-sm ${textSecondary}`}>{l.subtitle}</p>
            </div>
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium ${textMuted}`}>{stepLabel}</span>
                <span className={`text-xs font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>{Math.round(progress)}%</span>
              </div>
              <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-200'}`}>
                <div className="h-full rounded-full bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </>
        )}
        <div className={`rounded-2xl border p-6 sm:p-8 ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
          {renderStep()}
        </div>
        {!submitted && (
          <div className="flex items-center justify-between mt-6">
            <button onClick={prev} disabled={step === 0}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                step === 0 ? 'opacity-0 pointer-events-none' : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              <ArrowLeft className="w-4 h-4" /> {l.back}
            </button>
            {isLast ? (
              <button onClick={submit} disabled={!canNext() || submitting} data-testid="submit-btn"
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] shadow-lg shadow-[#7BA7C2]/20 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? l.submitting : l.submit}
              </button>
            ) : (
              <button onClick={next} disabled={!canNext()} data-testid="next-btn"
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] shadow-lg shadow-[#7BA7C2]/20 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100">
                {l.next} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PriceCalculator;
